import { Input } from '@/components/cms/ui/input';
import { Card, CardHeading } from '@/components/ui/card';
import LureoButton from '@/common/LureoButton';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { TextLink } from '@/components/ui/text-button';
import { CardTitle } from '@/components/ui/text';
import { Segmented } from '@/components/ui/segmented';
import type { ThemeMode } from '@/modules/theme';

interface SettingsCardProps {
  /** Light or dark, everywhere in Lureo, and what changes it. */
  appearance: ThemeMode;
  onAppearanceChange: (mode: ThemeMode) => void;
  /** Each switch's state, and what flips it. */
  marketingConsent: boolean;
  onToggleMarketing: () => void;
  watermark: boolean;
  onToggleWatermark: () => void;
  /** The org's Immersal token: whether one is saved, whether it is being replaced, and the one being typed. */
  immersalConfigured: boolean;
  immersalEditing: boolean;
  immersalToken: string;
  immersalSaving: boolean;
  onImmersalTokenChange: (token: string) => void;
  onImmersalReplace: () => void;
  onImmersalCancel: () => void;
  onImmersalSave: () => void;
  onImmersalRemove: () => void;
  onCookiePreferences: () => void;
  onSignOut: () => void;
  /** The switch whose write is running, which shows a spinner. */
  busy?: 'marketing' | 'watermark' | null;
  /** The switches and the token cannot change until the membership has loaded, nor while a switch writes. */
  togglesDisabled?: boolean;
  immersalDisabled?: boolean;
}

/** My Account's Settings tab: light or dark, emails, the Immersal watermark and token, cookies, and signing out. */
export function SettingsCard({
  appearance,
  onAppearanceChange,
  marketingConsent,
  onToggleMarketing,
  watermark,
  onToggleWatermark,
  immersalConfigured,
  immersalEditing,
  immersalToken,
  immersalSaving,
  onImmersalTokenChange,
  onImmersalReplace,
  onImmersalCancel,
  onImmersalSave,
  onImmersalRemove,
  onCookiePreferences,
  onSignOut,
  busy = null,
  togglesDisabled = false,
  immersalDisabled = false,
}: SettingsCardProps) {
  return (
    <Card elevation="high">
      <CardHeading title="Account Settings" description="Manage your account preferences" className="mb-6" />
      <div className="space-y-6">
        <div className="flex items-center justify-between rounded-lureo-radius-lg border-lureo-border-width p-4">
          <div>
            <h3 className="font-medium">Appearance</h3>
            <p className="text-lureo-14 text-lureo-ink-subtle leading-5">Light or dark, everywhere in Lureo</p>
          </div>
          <Segmented
            variant="pills"
            label="Appearance"
            value={appearance}
            onChange={onAppearanceChange}
            options={[{ id: 'light', label: 'Light' }, { id: 'dark', label: 'Dark' }]}
          />
        </div>

        <div className="flex items-center justify-between rounded-lureo-radius-lg border-lureo-border-width p-4">
          <div>
            <h3 className="font-medium">Marketing Emails</h3>
            <p className="text-lureo-14 text-lureo-ink-subtle leading-5">Receive product updates and marketing emails from Lureo</p>
          </div>
          <LureoButton
            variant="outline-3"
            className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
            disabled={togglesDisabled}
            onClick={onToggleMarketing}>
            {busy === 'marketing' && <LoadingSpinner size="sm" className="mr-2" />}
            {marketingConsent ? 'Disable' : 'Enable'}
          </LureoButton>
        </div>

        <div className="flex items-center justify-between rounded-lureo-radius-lg border-lureo-border-width p-4">
          <div>
            <h3 className="font-medium">Immersal Watermark in AR Experiences</h3>
            <p className="text-lureo-14 text-lureo-ink-subtle leading-5">Add a watermark to your AR experiences</p>
          </div>
          <LureoButton
            variant="outline-3"
            className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
            disabled={togglesDisabled}
            onClick={onToggleWatermark}>
            {busy === 'watermark' && <LoadingSpinner size="sm" className="mr-2" />}
            {watermark ? 'Disable' : 'Enable'}
          </LureoButton>
        </div>

        <div className="rounded-lureo-radius-lg border-lureo-border-width p-4">
          <div className="mb-3">
            <h3 className="font-medium">Immersal Developer Token</h3>
            <p className="text-lureo-14 text-lureo-ink-subtle leading-5">
              {immersalConfigured
                ? 'Saved. Immersal web stories work on Android.'
                : 'Add your token so Immersal web stories work on Android.'}
            </p>
          </div>
          {immersalConfigured && !immersalEditing ? (
            <div className="flex items-center justify-between gap-2">
              <Input
                type="password"
                autoComplete="new-password"
                value="••••••••••••"
                readOnly
                disabled
                className="bg-lureo-neutral/50 cursor-not-allowed max-w-xs"
              />
              <div className="flex items-center gap-2 shrink-0">
                <LureoButton
                  variant="outline-3"
                  className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
                  onClick={onImmersalReplace}
                >
                  Replace
                </LureoButton>
                <LureoButton
                  variant="outline-2"
                  className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
                  disabled={immersalSaving || immersalDisabled}
                  onClick={onImmersalRemove}
                >
                  {immersalSaving && <LoadingSpinner size="sm" className="mr-2" />}
                  Remove
                </LureoButton>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <Input
                type="password"
                autoComplete="new-password"
                value={immersalToken}
                onChange={(e) => onImmersalTokenChange(e.target.value)}
                placeholder="Paste your Immersal developer token"
                className="max-w-xs"
              />
              <div className="flex items-center gap-2 shrink-0">
                {immersalConfigured && (
                  <LureoButton
                    variant="outline-2"
                    className="h-9 px-4 w-24 rounded-lureo-radius-button-pill"
                    disabled={immersalSaving}
                    onClick={onImmersalCancel}
                  >
                    Cancel
                  </LureoButton>
                )}
                <LureoButton
                  variant="outline-3"
                  className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
                  disabled={immersalSaving || immersalDisabled || !immersalToken.trim()}
                  onClick={onImmersalSave}
                >
                  {immersalSaving && <LoadingSpinner size="sm" className="mr-2" />}
                  Save
                </LureoButton>
              </div>
            </div>
          )}
          <TextLink
            href="https://developers.immersal.com/"
            target="_blank"
            rel="noopener noreferrer"
            tone="quiet"
            size="sm"
            className="mt-2 underline"
          >
            Get your token from the Immersal Developer Portal
          </TextLink>
        </div>

        <div className="flex items-center justify-between rounded-lureo-radius-lg border-lureo-border-width p-4">
          <div>
            <h3 className="font-medium">Cookie preferences</h3>
            <p className="text-lureo-14 text-lureo-ink-subtle leading-5">Manage analytics and marketing cookies</p>
          </div>
          <LureoButton
            variant="outline-3"
            className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
            onClick={onCookiePreferences}
            suppressTooltip
          >
            Manage
          </LureoButton>
        </div>

        <div className="border-t-lureo-border-width pt-6">
          <div className="flex items-center justify-between rounded-lureo-radius-lg border-lureo-border-width p-4">
            <div>
              <CardTitle as="h3" className="leading-7 font-medium text-lureo-coral-ink">Account Actions</CardTitle>
              <p className="text-lureo-14 text-lureo-ink-subtle leading-5">Sign out of your account</p>
            </div>
            <LureoButton
              variant="danger"
              className="h-9 px-4 w-28 rounded-lureo-radius-button-pill"
              onClick={onSignOut}>Sign Out</LureoButton>
          </div>
        </div>
      </div>
    </Card>
  );
}
